import React, { useCallback, useEffect, useReducer } from 'react'
import PropTypes from 'prop-types'
import { DialogOverlay, DialogOverlayFooter, DialogOverlayActions, useOverlay, Button, Paragraph } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { useTranslation } from 'react-i18next'

function loadErrorTranslations() {
  const errorHeaders = {
    generic: i18n.t('indexing-error-overlay.generic.title', 'An Error Has Occurred'),
    permission: i18n.t('indexing-error-overlay.permission.title', 'Permission Issue'),
    poorQuality: i18n.t('indexing-error-overlay.poor.quality.title', 'Poor Image Quality'),
  }

  const errorMessages = {
    permission: i18n.t(
      'indexing-error-overlay.permission.message',
      "You don't currently have permission to perform this action. You may need to refresh or login."
    ),
    generic: i18n.t(
      'indexing-error-overlay.generic.message',
      'Click Retry to have the computer run it again. This report will be automatically sent to FamilySearch.'
    ),
    poorQuality: i18n.t(
      'indexing-error-overlay.poor.quality.message',
      "The computer can't read this image because of it's poor quality. Please try a different image."
    ),
  }
  return { errorHeaders, errorMessages }
}

function reducer(state, action) {
  switch (action.type) {
    case 'INIT': {
      return {
        ...state,
        ...action.value,
      }
    }
    case 'PERMISSION': {
      return {
        ...state,
        header: state.errorHeaders.permission,
        message: state.errorMessages.permission,
        retry: false,
      }
    }
    case 'POOR-QUALITY': {
      return {
        ...state,
        header: state.errorHeaders.poorQuality,
        message: state.errorMessages.poorQuality,
        retry: false,
      }
    }
    default: {
      throw new Error(`Unhandled type in ErrorOverlay reducer: ${action.type}`)
    }
  }
}

export default function IndexingErrorOverlay({ error }) {
  const [state, dispatch] = useReducer(reducer, {})
  const overlay = useOverlay()
  const [t] = useTranslation()

  useEffect(() => {
    if (!state.errorMessages) {
      const { errorHeaders, errorMessages } = loadErrorTranslations()
      dispatch({
        type: 'INIT',
        value: {
          errorHeaders,
          errorMessages,
          message: errorMessages.generic,
          header: errorHeaders.generic,
          retry: true,
        },
      })
    }
  }, [state.errorMessages])

  useEffect(() => {
    if (error) {
      const { response, config } = error
      if (config && config.url.includes('cogsworth')) dispatch({ type: 'POOR-QUALITY' })
      if (response && response.status === 404) dispatch({ type: 'PERMISSION' })
      if (response && response.status === 403) dispatch({ type: 'PERMISSION' })
      overlay.open()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const refreshPage = useCallback((event) => {
    event.currentTarget.disabled = true
    // eslint-disable-next-line no-restricted-globals
    location.reload()
  }, [])
  const goBackInHistory = useCallback((event) => {
    event.currentTarget.disabled = true
    window.history.back()
  }, [])

  return (
    <DialogOverlay title={state.header} {...overlay}>
      <Paragraph>{state.message}</Paragraph>

      <DialogOverlayFooter>
        <DialogOverlayActions>
          {state.retry && (
            <Button data-testid="cancel-button" onClick={overlay.close}>
              {t('common-ui:cancel.action', 'Cancel')}{' '}
            </Button>
          )}
          {state.retry && (
            <Button data-testid="retry-button" onClick={(event) => refreshPage(event)} emphasis="high">
              {t('indexing-error-overlay.retry.button', 'Retry')}
            </Button>
          )}
          {!state.retry && (
            <Button data-testid="continue-button" onClick={(event) => goBackInHistory(event)} emphasis="high">
              {t('continue.action', 'Continue')}
            </Button>
          )}
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

IndexingErrorOverlay.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
}
